import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Activity,
  Banknote,
  Fuel,
  Grid,
  Leaf,
  Percent,
  TrendingUp,
  Zap,
} from "lucide-react";

export const EnergyKPISection = ({ data }: any) => {
  const currency = useCurrency();
  // Mock data for energy KPIs
  const energyKPIs = [
    {
      title: "Total Energy Consumption",
      count: `${data?.energyConsumption?.total?.value ?? "NA"} ${
        data?.energyConsumption?.total?.unit ?? "NA"
      }`,
      icon: <Activity className="h-8 w-8 text-blue-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Grid Energy (MWh) = SUM(Grid Meter
            Readings for period)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>
                Querys all assets where Asset Type = "Utility Electric Meter"
              </li>
              <li>
                For each grid meter asset:
                <ul>
                  <li>
                    {
                      "Retrieve all readings where Reading Date >= Start Date AND Reading Date <= End Date"
                    }
                  </li>
                  <li>
                    Converts each reading to MWh using unit conversion rules
                  </li>
                  <li>Sums all converted readings for that asset</li>
                </ul>
              </li>
              <li>Sums the totals from all grid meter assets</li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: "Total consumption",
    },
    {
      title: "Grid Energy",
      count: `${data?.energyConsumption?.grid?.value ?? "NA"} ${
        data?.energyConsumption?.grid?.unit ?? "NA"
      }`,
      icon: <Grid className="h-8 w-8 text-purple-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Grid Energy (MWh) = SUM(Grid Meter
            Readings for period)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>
                Querys all assets where Asset Type = "Utility Electric Meter"
              </li>
              <li>
                For each grid meter asset:
                <ul>
                  <li>
                    {
                      "Retrieve all readings where Reading Date >= Start Date AND Reading Date <= End Date"
                    }
                  </li>
                  <li>
                    Converts each reading to MWh using unit conversion rules
                  </li>
                  <li>Sums all converted readings for that asset</li>
                </ul>
              </li>
              <li>Sums the totals from all grid meter assets</li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: `${data?.energyConsumption?.grid?.percentage}% of total`,
    },
    {
      title: "Renewable Energy",
      count: `${data?.energyConsumption?.renewable?.value ?? "NA"} ${
        data?.energyConsumption?.renewable?.unit ?? "NA"
      }`,
      icon: <Leaf className="h-8 w-8 text-green-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Renewable Energy (MWh) = SUM(Renewable
            Source Readings for period)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>
                Querys all assets where Asset Type = "Renewable Source"
                (includes Solar Panels, Wind Turbines)
              </li>
              <li>
                For each renewable source asset:
                <ul>
                  <li>
                    {
                      "Retrieve all readings where Reading Date >= Start Date AND Reading Date <= End Date"
                    }
                  </li>
                  <li>
                    Converts each reading to MWh using unit conversion rules
                  </li>
                  <li>Sums all converted readings for that asset</li>
                </ul>
              </li>
              <li>Sums the totals from all renewable source assets</li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: `${data?.energyConsumption?.renewable?.percentage}% of total`,
    },
    {
      title: "Generator Energy",
      count: `${data?.energyConsumption?.generator?.value ?? "NA"} ${
        data?.energyConsumption?.generator?.unit ?? "NA"
      }`,
      icon: <Fuel className="h-8 w-8 text-orange-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Generator Energy (MWh) = SUM(Generator
            Readings for period)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>Querys all assets where Asset Type = "Generator"</li>
              <li>
                For each generator asset:
                <ul>
                  <li>
                    {
                      "Retrieve all readings where Reading Date >= Start Date AND Reading Date <= End Date"
                    }
                  </li>
                  <li>
                    Converts each reading to MWh using unit conversion rules
                  </li>
                  <li>Sums all converted readings for that asset</li>
                </ul>
              </li>
              <li>Sum the totals from all generator assets</li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: `${data?.energyConsumption?.generator?.percentage}% of total`,
    },
  ];

  const costAndEfficiencyKPIs = [
    {
      title: "Total Energy Cost",
      count: `${currency} ${data?.energyCosts?.total?.value ?? "NA"}`,
      icon: <Banknote className="h-8 w-8 text-red-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Total Energy Cost ($) = Grid Cost +
            Renewable Cost + Generator Cost
          </li>
        </ul>
      ),
      trendSummary: "Monthly cost",
    },
    {
      title: "Average Rate",
      count: `${currency} ${data?.averageRate?.total?.value ?? "NA"} `,
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Average Rate ($/kWh) = Total Energy Cost /
            Total Energy Consumption (in kWh)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>
                Converts Total Energy Consumption to kWh:{" "}
                <code>Total (kWh) = Total (MWh) × 1,000</code>
              </li>
              <li>
                Divide:{" "}
                <code>Average Rate = Total Cost / Total Consumption (kWh)</code>
              </li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: "Blended rate",
    },
    {
      title: "Energy Efficiency",
      count: `${data?.energyEfficiency?.value ?? "NA"}`,
      icon: <Zap className="h-8 w-8 text-yellow-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Energy Efficiency (kWh/m³) = Total Energy
            Consumption (kWh) / Total Water Processed (m³)
          </li>

          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>
                Calculates Total Energy Consumption in kWh:{" "}
                <code>Total (kWh) = Total Energy (MWh) × 1,000</code>
              </li>
              <li>Calculates Total Water Processed in m³ for all entities</li>
              <li>
                Divide:{" "}
                <code>
                  Efficiency = Total Energy (kWh) / Total Water Processed (m³)
                </code>
              </li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: `${data?.waterProcessed?.waterProcessedM3 ?? "NA"} m³`,
    },
    {
      title: "Renewable Contribution",
      count: `${data?.renewableContribution?.value ?? "NA"}`,
      icon: <Percent className="h-8 w-8 text-green-600" />,
      tooltip: (
        <ul>
          <li>
            <strong>Formula:</strong> Renewable Contribution (%) = (Renewable
            Energy / Total Energy) × 100
          </li>
          <li>
            <strong>Step-by-step process:</strong>
            <ul>
              <li>Calculates Renewable Energy using BR-008</li>
              <li>Calculates Total Energy Consumption.</li>
              <li>
                Divide and multiply by 100:{" "}
                <code>Renewable % = (Renewable / Total) × 100</code>
              </li>
            </ul>
          </li>
        </ul>
      ),
      trendSummary: `${data?.renewableContribution?.subText ?? "NA"}`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Energy Consumption Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {energyKPIs.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            icon={card.icon}
            tooltip={card.tooltip}
          />
        ))}
      </div>

      {/* Cost and Efficiency Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {costAndEfficiencyKPIs.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            icon={card.icon}
            tooltip={card.tooltip}
          />
        ))}
      </div>
    </div>
  );
};
